/**
 * Barre d'outils posée sur la barre d'état, façon jeu de gestion (Cities:
 * Skylines) : à gauche les vues (épingles d'affluence, plan des pistes,
 * calque, carte, vue libre), au centre les rubriques de construction en
 * pictogrammes de couleur (le menu s'ouvre au-dessus), à droite la gestion
 * (pistes, damage, fréquentation, direction, journal, performances).
 * Raccourci sous chaque bouton ; libellé dans l'infobulle.
 */
import { hud } from './context';
import { dayJams, usageDay } from './report';
import { ui, type OfficeTab } from './ui-state';
import { BUILD_CATS, buildCat, catHasItems, type BuildCat } from './build-cats';
import { PgBook, PgBriefcase, PgCamera, PgChart, PgGroomer, PgLayers, PgOverview, PgPin, PgPulse, PgSignpost, PgTrailMap, type AnyIcon } from './icons';

function DockButton(props: { icon: AnyIcon; label: string; hotkey: string; on?: boolean; disabled?: boolean; onClick: () => void; badge?: number; tip?: string }) {
  const Icon = props.icon;
  return (
    <button
      type="button"
      class={`g-dock-btn${props.on ? ' on' : ''}`}
      aria-label={props.label}
      aria-pressed={props.on}
      data-tip={`${props.tip ?? props.label} (${props.hotkey})`}
      disabled={props.disabled}
      onClick={props.onClick}
    >
      <Icon size={19} />
      <span class="g-dock-key">{props.hotkey}</span>
      {props.badge ? <span class="g-dock-badge">{props.badge}</span> : null}
    </button>
  );
}

const toggleOffice = (tab: OfficeTab) => {
  ui.office.value = ui.office.value === tab ? null : tab;
};

function openCat(id: BuildCat): void {
  if (ui.buildMenu.value && buildCat.value === id && !ui.build.value) {
    ui.buildMenu.value = false;
    return;
  }
  buildCat.value = id;
  ui.build.value = null;
  ui.buildMenu.value = true;
}

export function Dock() {
  void ui.tick.value;
  const store = hud.store;
  const engine = hud.engine;
  const owner = !!store?.isOwner;
  const today = usageDay(store?.replica?.usage, null);
  const jams = dayJams(today).filter((j) => j.level >= 3).length;
  const open = ui.buildMenu.value;
  const mode = ui.build.value;
  // Catégorie surlignée : celle du menu ouvert, ou celle de l'objet en cours de placement.
  const active: BuildCat | null = !open ? null : mode?.kind === 'piste' ? 'piste' : mode?.kind === 'lift' ? 'lift' : buildCat.value;
  return (
    <div class="g-dock">
      <div class="g-dock-group g-dock-views">
        <DockButton icon={PgPin} label="Affluence" tip="Épingles d'affluence" hotkey="I" on={ui.pins.value} onClick={() => (ui.pins.value = !ui.pins.value)} />
        <DockButton icon={PgTrailMap} label="Plan des pistes" hotkey="L" on={ui.trailMap.value} onClick={() => (ui.trailMap.value = !ui.trailMap.value)} />
        <DockButton icon={PgLayers} label="Calque" tip="Calque des pistes" hotkey="K" on={ui.overlay.value} onClick={() => (ui.overlay.value = !ui.overlay.value)} />
        <DockButton icon={PgOverview} label="Carte" tip="Carte : vue d'ensemble du domaine" hotkey="M" onClick={() => engine?.cam.overview()} />
        <DockButton icon={PgCamera} label="Vue libre" hotkey="V" on={ui.freeCam.value} onClick={() => (ui.freeCam.value = !ui.freeCam.value)} />
      </div>
      {owner && (
        <div class="g-dock-group g-dock-cats" role="toolbar" aria-label="Construire">
          {BUILD_CATS.filter((c) => catHasItems(c.id)).map((c) => {
            const Icon = c.icon;
            return (
              <button
                type="button"
                key={c.id}
                class={`g-cat${active === c.id ? ' on' : ''}`}
                style={{ '--cat': c.color } as Record<string, string>}
                aria-label={`Construire : ${c.label}`}
                aria-pressed={active === c.id}
                data-tip={`${c.label} (B)`}
                onClick={() => openCat(c.id)}
              >
                <Icon size={26} />
              </button>
            );
          })}
        </div>
      )}
      <div class="g-dock-group g-dock-manage">
        <DockButton icon={PgSignpost} label="Pistes" tip="Gestion des pistes" hotkey="P" on={ui.pistesPanel.value} onClick={() => (ui.pistesPanel.value = !ui.pistesPanel.value)} />
        <DockButton
          icon={PgGroomer}
          label="Damage"
          tip="Damage de nuit"
          hotkey="N"
          on={ui.grooming.value}
          badge={[...(store?.replica?.groomers.values() ?? [])].filter((g) => g.status !== 'parked').length}
          onClick={() => {
            ui.grooming.value = !ui.grooming.value;
            if (ui.grooming.value) ui.selection.value = null;
          }}
        />
        <DockButton icon={PgChart} label="Fréquentation" tip="Fréquentation : quotas et bouchons" hotkey="F" on={ui.office.value === 'frequentation'} disabled={!owner} badge={jams} onClick={() => toggleOffice('frequentation')} />
        <DockButton
          icon={PgBriefcase}
          label="Direction"
          tip="Direction : finances, stocks, tarifs"
          hotkey="G"
          on={ui.office.value !== null && ui.office.value !== 'frequentation' && ui.office.value !== 'journal'}
          disabled={!owner}
          onClick={() => toggleOffice('finances')}
        />
        <DockButton icon={PgBook} label="Journal" hotkey="J" on={ui.office.value === 'journal'} disabled={!owner} onClick={() => toggleOffice('journal')} />
        <DockButton icon={PgPulse} label="Perf." tip="Performances" hotkey="F3" on={ui.perf.value} onClick={() => (ui.perf.value = !ui.perf.value)} />
      </div>
    </div>
  );
}
